/**
 * FeaturedHotelsCarousel
 *
 * Renders the admin-curated "Top Booked" (or other featured-list) hotels as a
 * horizontally-scrollable carousel with a gold "TOP SELLER" badge.
 * Data source: GET /api/featured-hotels/{listType}
 * Returns silently empty if the list has no active items.
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Star, MapPin, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeaturedHotelsCarousel = ({
  listType = 'top_booked',
  limit = 12,
  titleKey = 'featured.topBookedTitle',
  titleFallback = 'Most Booked Hotels',
  subtitleKey = 'featured.topBookedSubtitle',
  subtitleFallback = 'Most loved by our travelers',
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/featured-hotels/${listType}`, { params: { limit } })
      .then((res) => { if (alive) setItems(res.data?.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [listType, limit]);

  if (!loading && items.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="py-12 bg-gradient-to-b from-background to-secondary/20" data-testid="featured-hotels-section">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E8A317] to-[#FFD700] text-[#081D33] text-xs font-bold mb-2 shadow">
              <Sparkles className="w-3.5 h-3.5" />
              {t('featured.topSeller', 'TOP SELLER')}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {t(titleKey, titleFallback)}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t(subtitleKey, subtitleFallback)}
            </p>
          </div>
          <div className="hidden md:flex gap-2">
            <Button
              size="icon"
              variant="outline"
              className="rounded-full"
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
              data-testid="featured-scroll-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="rounded-full"
              onClick={() => scroll(1)}
              aria-label="Scroll right"
              data-testid="featured-scroll-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-thin"
          style={{ scrollPaddingLeft: '1rem' }}
          data-testid="featured-hotels-scroller"
        >
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="min-w-[260px] md:min-w-[300px] h-[340px] rounded-2xl bg-secondary/40 animate-pulse snap-start"
              />
            ))
          ) : (
            items.map((h) => (
              <Link
                key={h.hotel_id}
                to={`/hotel/${h.hotel_id}`}
                className="group relative min-w-[260px] md:min-w-[300px] max-w-[300px] rounded-2xl bg-card border border-border overflow-hidden snap-start hover:shadow-xl hover:-translate-y-0.5 transition-all"
                data-testid={`featured-card-${h.hotel_id}`}
              >
                {/* Gold corner ribbon */}
                <div className="absolute top-3 left-3 z-10 px-2 py-1 rounded-full bg-gradient-to-r from-[#E8A317] to-[#FFD700] text-[#081D33] text-[10px] font-bold shadow-md flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {t('featured.topSeller', 'TOP SELLER')}
                </div>

                {/* Image — falls back via API endpoint if upstream fails */}
                <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                  <img
                    src={h.image || `${API}/public/hotel-fallback-image?width=400`}
                    alt={h.hotelname}
                    loading="lazy"
                    onError={(e) => {
                      // Avoid infinite loop: only swap once
                      if (!e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = `${API}/public/hotel-fallback-image?width=400`;
                      }
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Body */}
                <div className="p-4">
                  <h3 className="font-semibold text-base text-foreground line-clamp-1">
                    {h.hotelname}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="line-clamp-1">
                      {h.city_localized || h.city}, {h.country_localized || h.country_name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.max(0, Math.min(5, parseInt(h.star_rate) || 0)) }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-[#E8A317] text-[#E8A317]" />
                      ))}
                    </div>
                    {h.review_score ? (
                      <span className="text-xs font-semibold text-primary">
                        {Number(h.review_score).toFixed(1)} / 10
                      </span>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedHotelsCarousel;
